<template>
  <section>
    <Loader
      :loader="formLoader"
      label="Carregando..."
      loaderType="progress"
      loaderSize="h-96"
    />
    <section class="bg-h-primary-50 border py-2">
      <div class="container mx-auto">
        <BreadcrumbComponent :home="home" :items="items" />
      </div>
    </section>
    <div
      v-if="!formLoader"
      class="container mx-auto w-12/12 sm:w-10/12 lg:w-6/12 my-10"
    >
      <form
        @submit.prevent="submitObjective()"
        class="grid grid-cols-12 items-center gap-x-3"
        autocomplete="off"
      >
        <div class="flex justify-between items-center col-span-12">
          <h4>
            {{
              $t("labels.generic", { pt: "Novo objetivo", en: "New objective" })
            }}
          </h4>
          <div class="flex gap-3">
            <Button
              id="objective-cancel-btn"
              :label="$t('labels.cancel')"
              class="btn-outline-bg-white p-button-sm"
              :loading="formLoader"
              @click="confirmModal()"
            />
            <Button
              id="objective-save-btn"
              :label="$t('labels.save')"
              type="submit"
              class="btn-primary p-button-sm"
              :loading="btnLoader"
            />
          </div>
        </div>

        <div class="my-4 col-span-12">
          <p class="mb-2">{{ $t("objectives.inputs.name") }}</p>
          <InputText
            id="objective-save-input"
            type="text"
            name="name"
            v-model="infoData.name"
            :class="v$.infoData.name.$error ? 'p-invalid' : ''"
            :maxlength="50"
          />
          <Errors
            :errors="v$.infoData.name.$errors"
            :actualLength="infoData.name?.length"
            :maxLength="50"
          />
        </div>
        <div class="my-4 col-span-12">
          <p class="mb-2">{{ $t("objectives.inputs.description") }}</p>
          <Textarea
            id="objective-description-input"
            :autoResize="true"
            rows="10"
            cols="30"
            v-model="infoData.description"
            :class="v$.infoData.description.$error ? 'p-invalid' : ''"
            :maxlength="999"
          />
          <Errors
            :errors="v$.infoData.description.$errors"
            :actualLength="infoData.description?.length"
            :maxLength="999"
          />
        </div>
      </form>
    </div>
    <ConfirmDialog :style="{ width: '80vw', maxWidth: '700px' }" />
  </section>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { required } from "@/locale/locale.config";
import { Objective } from "@/models/objective.model";
import useVuelidate from "@vuelidate/core";
import { ObjectivesRest } from "@/services/rest/objectives.rest";
import { GoToRoute } from "@/utils/go-to.util";
import { getItemLocalStorage } from "@/utils/save-storage.util";
import { NewObjectiveService } from "./new-objective.service";
import { take, last } from "rxjs";

export default defineComponent({
  name: "newObjective",
  validations() {
    return {
      infoData: {
        name: { required },
        description: { required },
      },
    };
  },
  computed: {
    rest(): ObjectivesRest {
      return new ObjectivesRest();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
    service(): NewObjectiveService {
      return new NewObjectiveService();
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  mounted() {
    if (this.$route.params.objectiveId) {
      this.getObjective();
    }
  },
  data() {
    return {
      infoData: new Objective(),
      formLoader: false as boolean,
      home: {
        label: this.getChapterName().name,
        command: () => this.$router.go(-1),
      },
      items: [
        {
          label: !this.$route.params?.objectiveId
            ? this.$t("labels.generic", {
                pt: "Novo objetivo",
                en: "New objective",
              })
            : this.$t("labels.generic", {
                pt: "Editar objetivo",
                en: "Edit objective",
              }),
        },
      ],
      params: {
        courseId: String(this.$route.params.courseId),
        chapterId: String(this.$route.params.chapterId),
        tab: 1,
      },
      btnLoader: false as boolean,
    };
  },
  methods: {
    submitObjective(): void {
      this.v$.infoData.$touch();
      if (!this.v$.infoData.$error) {
        this.btnLoader = true;
        const data = {
          name: this.infoData.name,
          description: this.infoData.description,
          predecessorId: this.infoData.predecessorId ?? this.getPredecessor(),
        };
        if (this.infoData.id) {
          this.editObjective(data, this.infoData.id);
        } else {
          this.createObjective(data);
        }
      }
    },
    createObjective(data: any): void {
      this.rest
        .postObjectiveTraditional(
          this.params.courseId,
          this.params.chapterId,
          data
        )
        .subscribe({
          next: () => {
            this.$router.go(-1);
          },
        });
    },
    editObjective(data: any, objectiveId: string): void {
      this.rest
        .updateObjective(this.params.chapterId, objectiveId, data)
        .subscribe({
          next: () => {
            this.$router.go(-1);
          },
        });
    },
    confirmModal(event?: any): void {
      this.$confirm.require({
        message: this.$t("labels.generic", {
          pt: `Os dados não salvos serão perdidos! Tem certeza que desejar sair?`,
          en: `Unsaved data will be lost! Are you sure you want to quit?`,
        }),
        header: this.$t("labels.generic", {
          pt: "Sair da tela",
          en: "Exit Screen",
        }),
        acceptLabel: "Sim",
        rejectLabel: "Não",
        rejectClass: "btn-outline",
        acceptClass: "btn-primary",
        accept: () => {
          this.$router.go(-1);
        },
      });
    },
    getChapterName(): void {
      return JSON.parse(getItemLocalStorage("objective") as string);
    },
    redirectToPage(): void {
      this.go.redirectToPage("formChapterTraditional", this.params);
    },
    getObjective(): void {
      this.service.objective.pipe(take(2), last()).subscribe({
        next: (response) => {
          this.infoData = response;
        },
      });
      this.service.getObjectiveById(
        String(this.$route.params.chapterId),
        String(this.$route.params.objectiveId)
      );
    },
    getPredecessor(): string {
      let result = "";
      const chapter = JSON.parse(getItemLocalStorage("objective"));
      if (chapter.trails.length > 0) {
        result = chapter.trails[chapter.trails.length - 1].id;
      }
      return result;
    },
  },
});
</script>

<style></style>
